import MLoginPopup from "./MLoginPopup";
import VLoginPopup from "./VLoginPopup";
import { LoginContext } from "./types/LoginContext";
import NanomonxForm from "../../components/Forms/NanomonxForm";
import NanoPopup from "../../components/Popups/NanoPopup";
import RestPromise from "../../utils/RestPromise";
import { HostDetector } from "../../utils/HostDetector";
import FamilyEmailGate from "./FamilyEmailGate";
import "../../../resources/less/Admin/Login/FamilyLoginCard.less";

/** The email gate is the whole family login, its password panel included, whichever host opens it. */
export const FAMILY_STEPS = ["emailgate"];

export class CLoginPopup extends NanoPopup {
  private _form: NanomonxForm | null = null;
  private readonly _model: MLoginPopup;
  private readonly _view: VLoginPopup;
  private readonly _familyGate: FamilyEmailGate | null;

  constructor() {
    super({
      target: "#troub-login-popup",
      disabledBackPanel: true,
    });

    this._view = new VLoginPopup(this.currentTarget);
    this._model = new MLoginPopup();
    this._familyGate = FamilyEmailGate.Build(
      this.currentTarget.find(".family-email-gate"),
      () => this.switchToSchool(),
      (email) => this.switchToSchoolAs(email),
      new URL(window.location.href).searchParams.get("email") ?? "",
    );

    this.initializeEventListeners();
    this.initializeForm();
  }

  public get context() {
    return this._model.context;
  }

  public Reset(): void {
    this._model.Reset();
    this._familyGate?.Reset();
  }

  public GoToStep(step: string): void {
    this._model.GoToStep(step);
  }

  public GetCurrentStep(): string {
    return this._model.currentStep;
  }

  public HasValidationErrors(): boolean {
    return this._view.HasValidationErrors();
  }

  /** The failed login the page reloaded with was tried on the family gate. */
  public FailedOnFamilyGate(): boolean {
    return this._familyGate?.HasFailedLogin() ?? false;
  }

  public Show(): void {
    this.GoToStep(this.GetCurrentStep());
    super.Show();
    if (this.GetCurrentStep() === "emailgate") {
      this._familyGate?.Open();
    }
  }

  public Hide(): void {
    super.Hide();
    this.Reset();
  }

  public SetContext(context: LoginContext, steps?: string[]): void {
    const contextSteps =
      steps ||
      (context === LoginContext.SCHOOL ? ["userrole", "login"] : FAMILY_STEPS);

    this._model.SetContext(context, contextSteps);
    this._view.SetContext(context);
    // The family popup is the prototype's card; the school one keeps the Troubadour chrome
    this.currentTarget.toggleClass(
      "family-login-card",
      context === LoginContext.FAMILY,
    );

    let host: string;
    if (context === LoginContext.FAMILY) {
      host = "family";
    } else if (context === LoginContext.SCHOOL) {
      if (HostDetector.IsConstellation()) {
        //We now only keep the Constellation host if we're on the constellation subdomain
        host = "constellation";
      } else {
        host = "troubadour";
      }
    } else {
      //Fallback to Troubadour if we don't know the context
      host = "troubadour";
    }

    // Remembers which product to land on after the login; a lost call only costs that landing
    void RestPromise.Create("/rest/set-redirection-host", "PUT", {
      host: host,
    }).catch(() => undefined);

    this.GoToStep(this.GetCurrentStep());
  }

  public UpdateUserType(userType: "TEACHER" | "STUDENT" | "CHILD" | "PARENT") {
    this._model.selectedUserType = userType;
    this._view.SetUserType(this._model.selectedUserType);
    this.proceedToNextStep();
  }

  private initializeEventListeners(): void {
    this.bindModelEvents();
    this.bindViewEvents();
  }

  private bindModelEvents(): void {
    this._model.On("stepChanged", (change) => {
      const { newStep } = change as { newStep: string };
      // « Étape précédente » from the school side lands on the gate: the family card comes back with it
      if (
        newStep === "emailgate" &&
        this._model.context !== LoginContext.FAMILY
      ) {
        this.SetContext(LoginContext.FAMILY, FAMILY_STEPS);
      }
      this._view.ShowStep(newStep);

      if (this._model.ShouldShowBackLink()) {
        this._view.ShowBackLink();
      } else {
        this._view.HideBackLink();
      }
    });

    this._model.On("reset", () => {
      this._view.ResetForm();
      this._view.HideAllMessages();
      this._view.HideBackLink();
    });
  }

  private bindViewEvents(): void {
    // Platform selection
    this._view.platformSelectButton.on("click", (event) => {
      const platform = $(event.currentTarget).attr("data-platform");
      if (platform) {
        this.handlePlatformSelection(platform);
      }
    });

    // User type selection (teacher/student buttons)
    this._view.teacherSelectButton.on("click", () => {
      this.UpdateUserType("TEACHER");
    });

    this._view.studentSelectButton.on("click", () => {
      this.UpdateUserType("STUDENT");
    });

    // Navigation buttons
    this._view.backStepButton.on("click", () => {
      this._model.GoToPreviousStep();
    });

    // Form submission
    this._view.submitButton.on("click", () => {
      this._view.SubmitForm();
    });

    // Create account
    this._view.createAccountButton.on("click", () => {
      this.handleCreateAccount();
    });

    // Message handling
    this._view.askTeacherButton.on("click", () => {
      this._view.ShowMessage("ask-teacher-message");
    });

    this._view.backFormButton.on("click", () => {
      this._view.HideMessage("ask-teacher-message");
    });

    this._view.targetMessageLink.on("click", (event) => {
      const targetMsg = $(event.currentTarget).attr("data-target-msg");
      if (targetMsg) {
        this.handleShowMessage(targetMsg);
      }
    });

    this._view.backCancelButton.on("click", () => {
      this._view.HideAllMessages();
    });

    this._view.closeContainer.on("click", () => {
      this.Hide();
      this._view.HideAllMessages();
    });
  }

  private handleShowMessage(targetMsg: string) {
    if (!this._model.steps.includes("accountcreationwarning")) {
      this._model.steps.push("accountcreationwarning");
    }
    this.GoToStep("accountcreationwarning");

    this._view.ShowMessage(targetMsg);
  }

  private handlePlatformSelection(platform: string): void {
    this._model.SetSelectedPlatform(platform);
    this._view.SetSelectedPlatform(platform);

    if (platform === "family") {
      this.SetContext(LoginContext.FAMILY, FAMILY_STEPS);
    } else {
      this.SetContext(LoginContext.SCHOOL, ["userrole", "login"]);
    }
    this.GoToStep(this._model.steps[0]);
  }

  /** The family card has no account link: the gate is where a family account starts. */
  private handleCreateAccount(): void {
    if (!this._model.steps.includes("accountcreation")) {
      this._model.steps.push("accountcreation");
    }
    this.GoToStep("accountcreation");
  }

  /** « Basculer version école » on the gate: the school login, starting with who the visitor is. */
  private switchToSchool(): void {
    this.SetContext(LoginContext.SCHOOL, ["userrole", "login"]);
    this.GoToStep("userrole");
  }

  /**
   * « Aller à la version scolaire » for an account the gate knows: the teacher form, its email
   * in place, « Étape précédente » leading back to who the visitor is.
   */
  private switchToSchoolAs(email: string): void {
    this.switchToSchool();
    this.UpdateUserType("TEACHER");
    this._view.PrefillUsername(email);
  }

  private proceedToNextStep(): void {
    const currentStepIndex = this._model.steps.indexOf(this._model.currentStep);
    if (currentStepIndex < this._model.steps.length - 1) {
      this._model.GoToNextStep();
    }
  }

  private initializeForm(): void {
    this._form = new NanomonxForm({
      elementName: "form-signin-popup",
      submitClass: ".submit-login-button",
      withValidation: true,
      allowMultiSubmitBtn: this._model.context === LoginContext.FAMILY,
    });
  }
}
